import { Link } from "react-router-dom";
import { Sparkles, Wand2, ArrowRight, Plus } from "lucide-react";

const familyLineup = [
  { name: "Pineapple Chicky", image: "/buddies/pineapple-chicky.jpg", rotate: "-rotate-[8deg]", color: "#FFD54F" },
  { name: "Captain Sammy", image: "/buddies/captain-sammy.png", rotate: "rotate-[4deg]", color: "#4DD0E1" },
  { name: "Sally Salad", image: "/buddies/sally-salad.png", rotate: "-rotate-[3deg]", color: "#81C784" },
];

export default function CreateYourOwnPromo() {
  return (
    <section id="create" data-testid="section-create-promo" className="relative py-16 sm:py-20 md:py-28 bg-white overflow-hidden scroll-mt-24">
      <div className="blob bg-pb-coral" style={{ width: 320, height: 320, top: 40, right: -120, opacity: 0.18 }} />
      <div className="blob bg-pb-sun" style={{ width: 320, height: 320, bottom: -80, left: -80, opacity: 0.3 }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-8 relative z-10">
        <div
          className="rounded-[2rem] sm:rounded-[3rem] p-5 sm:p-10 md:p-16 text-white shadow-[0_30px_80px_rgba(78,44,128,0.35)] relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, #5B3F94 0%, #4E2C80 45%, #06A5A1 100%)",
          }}
        >
          {/* Decorative shapes — desktop only to avoid mobile clutter */}
          <div className="hidden md:block absolute right-10 top-10 h-20 w-20 rounded-3xl bg-white/10 backdrop-blur rotate-12" />
          <div className="hidden md:block absolute left-10 bottom-10 h-14 w-14 rounded-2xl bg-pb-sun/30 backdrop-blur -rotate-12" />
          <div className="absolute -right-24 -bottom-24 h-80 w-80 rounded-full bg-pb-sun/20 blur-3xl" />
          <div className="absolute -left-16 top-1/3 h-48 w-48 rounded-full bg-pb-coral/20 blur-3xl" />

          <div className="relative z-10 grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center">
            <div className="min-w-0">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1.5 sm:px-4 text-[10px] sm:text-xs font-extrabold uppercase tracking-widest border border-white/30 text-white">
                <Sparkles className="h-3.5 w-3.5" /> Create Your Own Buddy
              </span>
              <h2 data-testid="create-promo-heading" className="font-display font-black text-[26px] sm:text-4xl md:text-5xl mt-4 sm:mt-5 leading-[1.05] text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.25)] break-words">
                Who will <span className="text-pb-sun">your kid</span> add to the Plate Buddies family?
              </h2>
              <p className="mt-4 sm:mt-5 text-white text-base sm:text-lg max-w-md leading-relaxed drop-shadow-[0_1px_4px_rgba(0,0,0,0.2)]">
                Kids describe a Buddy in their own words — we illustrate it in seconds and pair it with a kid-friendly recipe. Creativity, food curiosity, and ownership in one tiny ritual.
              </p>
              <Link
                to="/create"
                data-testid="btn-go-create"
                className="mt-6 sm:mt-8 inline-flex items-center gap-2 rounded-full bg-pb-sun text-pb-ink font-extrabold px-6 sm:px-7 py-3.5 sm:py-4 text-sm sm:text-base hover:scale-105 transition shadow-[0_12px_28px_rgba(0,0,0,0.28)]"
              >
                <Wand2 className="h-4 w-4" /> Build Your First Buddy
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {/* Family lineup → empty slot for "your" buddy.
             * Mobile: flex-wrap so 4 cards wrap to 2 rows and never overflow the viewport.
             * Desktop: single row lineup preserved via sm:flex-nowrap.
             * min-w-0 on the grid child prevents flex from expanding past the parent. */}
            <div className="relative flex items-center justify-center pt-2 pb-10 sm:pb-12 min-w-0">
              <div className="flex flex-wrap sm:flex-nowrap items-end justify-center gap-2 sm:gap-4 w-full max-w-full">
                {familyLineup.map((b, i) => (
                  <Link
                    key={b.name}
                    to={`/#buddies`}
                    data-testid={`promo-family-${i}`}
                    aria-label={`Meet ${b.name}`}
                    className={`group relative bg-white rounded-2xl shadow-[0_12px_30px_rgba(0,0,0,0.25)] ${b.rotate} w-[72px] sm:w-24 md:w-28 flex-shrink-0 animate-float-slow border-4 border-white hover:scale-110 hover:-translate-y-1 hover:rotate-0 transition-transform duration-300 cursor-pointer focus:outline-none focus:ring-4 focus:ring-pb-sun`}
                    style={{ animationDelay: `${i * 0.4}s` }}
                  >
                    <div
                      className="aspect-square rounded-t-xl overflow-hidden"
                      style={{ backgroundColor: b.color }}
                    >
                      <img
                        src={b.image}
                        alt={b.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="text-center font-display font-black text-pb-ink text-[9px] sm:text-xs py-1.5 px-1 truncate group-hover:text-pb-coral transition">
                      {b.name}
                    </p>
                  </Link>
                ))}

                {/* The empty "your buddy" slot — links to /create */}
                <Link
                  to="/create"
                  data-testid="promo-your-buddy-slot"
                  aria-label="Make your own Buddy"
                  className="group relative rounded-2xl shadow-[0_18px_40px_rgba(0,0,0,0.35)] rotate-[6deg] w-[82px] sm:w-28 md:w-32 flex-shrink-0 border-4 border-pb-sun animate-float-medium bg-white/10 backdrop-blur scale-105 sm:scale-110 hover:scale-[1.18] hover:rotate-0 transition-transform duration-300 cursor-pointer focus:outline-none focus:ring-4 focus:ring-pb-sun"
                >
                  <div
                    className="aspect-square rounded-t-xl overflow-hidden flex flex-col items-center justify-center relative"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(45deg, rgba(255,255,255,0.15) 0 6px, transparent 6px 12px)",
                      backgroundColor: "rgba(78,44,128,0.4)",
                    }}
                  >
                    <Plus className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 text-pb-sun group-hover:scale-125 transition-transform" strokeWidth={3} />
                    <span className="text-[9px] sm:text-xs font-extrabold uppercase tracking-widest text-white mt-1">
                      Yours
                    </span>
                  </div>
                  <p className="text-center font-display font-black text-pb-sun text-[9px] sm:text-xs py-1.5 px-1 bg-pb-ink/20">
                    Make your own
                  </p>
                  {/* Sparkle accent */}
                  <div className="absolute -top-2.5 -right-2.5 sm:-top-3 sm:-right-3 h-7 w-7 sm:h-9 sm:w-9 rounded-full bg-pb-coral flex items-center justify-center shadow-lg border-2 border-white group-hover:rotate-12 transition-transform">
                    <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                  </div>
                </Link>
              </div>

              {/* "Join the family" tag — sits below the row inside the reserved
                  pb-10/pb-12 padding so it never overlaps when cards wrap. */}
              <Link
                to="/#join"
                data-testid="promo-join-family"
                className="absolute bottom-1 sm:bottom-2 left-1/2 -translate-x-1/2 bg-pb-sun text-pb-ink font-display font-black rotate-[-3deg] rounded-xl px-3 sm:px-4 py-1.5 text-[11px] sm:text-sm shadow-[0_8px_20px_rgba(0,0,0,0.25)] border-[3px] border-white whitespace-nowrap hover:scale-105 hover:rotate-0 transition cursor-pointer"
              >
                Join the family
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
